/**
 * ClientEmailThreadCard — card thread email nella timeline cliente (Notion-like,
 * accento blu Gmail come EmailNoteCard). Effetto "pila" quando il thread ha piu
 * messaggi non ancora espansi; espansione carica i corpi lazy via
 * fetchClientMailThreadMessages (Task 9).
 *
 * Design doc: MD/features/2026-07-24-email-storico-scheda-cliente-design.md
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Loader2, Mail, Paperclip, Users } from 'lucide-react';
import { SanitizedHtml } from '../mail/SanitizedHtml';
import {
  fetchClientMailThreadMessages,
  type ClientMailThread,
  type ClientMailMessage,
} from '../../lib/services/clientMailService';
import { useGmailStore } from '../../lib/stores/gmailStore';
import { useOutlookStore } from '../../lib/stores/outlookStore';
import { useGmailDrawerStore } from '../../lib/stores/gmailDrawerStore';
import { useOutlookDrawerStore } from '../../lib/stores/outlookDrawerStore';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import { TimelineFooter } from '../shared/timeline/TimelineFooter';
import { CardMedia } from '../shared/timeline/CardMedia';
import {
  mediaTileCls,
} from '../shared/timeline/cardStyles';

/** Chiave stabile di un messaggio nella lista (provider + id). */
const messageKey = (m: ClientMailMessage) => `${m.provider}-${m.message_id}`;

/** Iniziale per l'avatar del mittente (nome se c'e', altrimenti email). */
const senderInitial = (m: ClientMailMessage) =>
  (m.from_name || m.from_email || '?').trim().charAt(0).toUpperCase();

/** Prima riga "pulita" del corpo/snippet per l'anteprima del messaggio collassato. */
const messagePreview = (m: ClientMailMessage) =>
  (m.snippet || m.body_text || '').replace(/\s+/g, ' ').trim();

interface ClientEmailThreadCardProps {
  thread: ClientMailThread;
  clientId: string;
  currentUserId: string | null;
  t: any;
  formatDate: (dateString: string) => string;
}

export const ClientEmailThreadCard: React.FC<ClientEmailThreadCardProps> = ({
  thread,
  clientId,
  currentUserId,
  t,
  formatDate,
}) => {
  const tr = t.emailTimeline ?? {};
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<ClientMailMessage[] | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Messaggi aperti nella vista espansa (modello Gmail: solo l'ultimo aperto di default)
  const [openMessages, setOpenMessages] = useState<Set<string>>(new Set());

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && messages === null && !loadingMessages) {
      setLoadError(false);
      setLoadingMessages(true);
      try {
        const loaded = await fetchClientMailThreadMessages(clientId, thread.sources);
        setMessages(loaded);
        const last = loaded[loaded.length - 1];
        setOpenMessages(new Set(last ? [messageKey(last)] : []));
      } catch {
        setLoadError(true);
      } finally {
        setLoadingMessages(false);
      }
    }
  };

  const toggleMessage = (key: string) => {
    setOpenMessages((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Il thread e' apribile nel client mail integrato solo se esiste nella casella
  // dell'utente corrente (il drawer mostra la propria casella; da li' si risponde).
  const ownSource = currentUserId
    ? thread.sources.find((s) => s.user_id === currentUserId)
    : undefined;

  const openInMailDrawer = async () => {
    if (!ownSource || !currentUserId) return;
    const orgId = await getCurrentUserOrganizationId();
    if (!orgId) return;
    if (ownSource.provider === 'gmail') {
      useGmailDrawerStore.getState().open();
      void useGmailStore.getState().selectThread(ownSource.thread_id, orgId, currentUserId);
    } else {
      useOutlookDrawerStore.getState().open();
      void useOutlookStore.getState().selectThread(ownSource.thread_id, orgId, currentUserId);
    }
  };

  const stacked = thread.message_count > 1 && !expanded;

  return (
    <div className="relative mb-3">
      {/* Effetto pila: due card sfalsate dietro quando il thread ha piu messaggi */}
      {stacked && (
        <>
          <div className="absolute inset-x-2 -bottom-1.5 h-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white dark:border-[#3F4447] dark:bg-[#2F3437]" />
          <div className="absolute inset-x-1 -bottom-[3px] h-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white dark:border-[#3F4447] dark:bg-[#2F3437]" />
        </>
      )}
      {/* Card piatta come le note/attivita' della timeline (niente ombra, niente accento colorato) */}
      <div className="relative overflow-hidden rounded-lg border border-[rgba(55,53,47,0.16)] bg-white transition-colors hover:border-[rgba(55,53,47,0.26)] dark:border-[#3F4447] dark:bg-[#2F3437] dark:hover:border-[#565D61]">
        <div className="min-w-0 p-3">
          <button onClick={toggle} aria-expanded={expanded} className="block w-full text-left">
            {/* Anatomia comune: immagine, oggetto, anteprima, poi la riga di chiusura */}
            <CardMedia
              media={
                <span className={mediaTileCls('email')}>
                  <Mail size={15} strokeWidth={1.5} />
                </span>
              }
            >
              <div className="truncate text-sm font-medium text-[#37352F] dark:text-white" title={thread.subject || tr.noSubject}>
                {thread.subject || tr.noSubject}
              </div>
              {thread.snippet && (
                <div className="mt-0.5 truncate text-xs text-[#787774] dark:text-white/50">
                  {thread.snippet}
                </div>
              )}
            </CardMedia>
            <TimelineFooter
              tipo="Email"
              accent="email"
              date={thread.last_message_at}
              titoloData={formatDate(thread.last_message_at)}
              expanded={expanded}
              left={
                <>
                  {thread.has_attachments && <Paperclip size={11} className="shrink-0 text-[#9B9A97]" />}
                  <span className="text-[10px] text-[#9B9A97] dark:text-white/40">
                    {thread.message_count} {tr.messages}
                  </span>
                  {thread.mailboxes.length > 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-[#9B9A97] dark:text-white/40">
                      <Users size={11} />
                      {tr.mailboxOf} {thread.mailboxes.map((m) => m.name).filter(Boolean).join(', ')}
                    </span>
                  )}
                </>
              }
            />
          </button>

          {expanded && (
            <div className="-mx-3 mt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
              {loadingMessages && (
                <div className="flex items-center gap-2 px-3 py-3 text-xs text-[#787774] dark:text-white/50">
                  <Loader2 size={13} className="animate-spin" />
                </div>
              )}
              {loadError && <div className="px-3 py-3 text-xs text-red-500">{tr.loadError}</div>}

              {(messages ?? []).map((m, i) => {
                const key = messageKey(m);
                const open = openMessages.has(key);
                const isLast = i === (messages?.length ?? 0) - 1;
                return (
                  <div
                    key={key}
                    className={`${i > 0 ? 'border-t border-[rgba(55,53,47,0.06)] dark:border-[#3F4447]/60' : ''}`}
                  >
                    {/* Riga messaggio (modello Gmail): avatar, mittente, anteprima, data */}
                    <button
                      onClick={() => toggleMessage(key)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03]"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EFEEEC] text-[11px] font-medium text-[#787774] dark:bg-[#3F4447] dark:text-white/60">
                        {senderInitial(m)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-[13px] ${open ? 'font-semibold' : 'font-medium'} text-[#37352F] dark:text-white`}>
                          {m.from_name || m.from_email}
                        </span>
                        {!open && (
                          <span className="block truncate text-xs text-[#787774] dark:text-white/45">
                            {messagePreview(m)}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[11px] tabular-nums text-[#787774] dark:text-white/45">
                        {formatDate(m.date)}
                      </span>
                    </button>

                    {open && (
                      <div className="min-w-0 px-3 pb-3 pl-[46px]">
                        {m.body_html ? (
                          <div className={`${isLast ? 'max-h-[26rem]' : 'max-h-72'} overflow-y-auto overflow-x-hidden`}>
                            <SanitizedHtml html={m.body_html} className="text-[13px]" />
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-[#37352F] dark:text-white/85">
                            {m.body_text || m.snippet || ''}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {ownSource && messages !== null && (
                <div className="border-t border-[rgba(55,53,47,0.06)] px-3 py-2 dark:border-[#3F4447]/60">
                  <button
                    onClick={openInMailDrawer}
                    className="inline-flex items-center gap-1 text-xs text-[#787774] hover:text-[#37352F] dark:text-white/50 dark:hover:text-white"
                  >
                    <ExternalLink size={12} />
                    {ownSource.provider === 'gmail' ? tr.openInGmail : tr.openInOutlook}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
